import { createContext, useContext } from "react";

import {
  triggersForEventTypeSelector,
  triggersForNodeSelector,
  type HandlerDetails,
  type HandlerProperties,
  type ResponseProperties,
  type TriggerCase,
  type TriggerConditionBuild,
  type TriggerDetails,
  type TriggerProperties,
} from "@skilitics/nautilus-core-designer-data";

import { useNautilusSelector, type GetRuntimeTriggers, type GetTriggerEvents } from "../state/index.ts";
import type { DesignerNode, NodeDesignerDescr, TriggersComponent } from "../types/index.ts";
import type { GetConditionBuildable } from "./conditions/index.ts";
import type {
  GetNodeConditionBuildables,
  GetNodeEffectBuildables,
  ResolveConditionBuildables,
  ResolveEffectBuildables,
} from "./querying/index.ts";

export type ResponseDetails = ResponseProperties & { id: number; catchAll?: boolean; disabled?: boolean };

export type TriggerDesignsContext = Readonly<{
  getRuntimeTriggers: GetRuntimeTriggers;
  getTriggerEvents: GetTriggerEvents;
  getResponse: (responseId: number) => ResponseDetails;
  getResponses: (handlerId: number) => ResponseDetails[];
  getTrigger: (triggerId: number) => TriggerDetails;
  getTriggersForEventType: (nodeId: number, eventType: string) => TriggerDetails[];
  getNodeIdByTriggerId: (triggerId: number) => number;
  getHaveTriggers: (nodeId: number) => boolean;
  addTrigger: (
    nodeId: number,
    eventType: string,
    where?: TriggerConditionBuild,
  ) => { triggerId: number; handlerId: number; responseId: number };
  updateTrigger: (triggerId: number, properties: TriggerProperties) => void;
  removeTrigger: (triggerId: number) => void;
  removeAllTriggers: (nodeId: number) => void;
  reorderTrigger: (triggerId: number, newIndex: number) => void;
  addTriggerCase: (triggerId: number, triggerCase: TriggerCase) => void;
  updateTriggerCase: (triggerId: number, index: number, updates: Partial<TriggerCase>) => void;
  removeTriggerCase: (triggerId: number, index: number) => void;
  addHandler: (triggerId: number) => number;
  getHandler: (handlerId: number) => HandlerDetails;
  getHandlers: (triggerId: number) => HandlerDetails[];
  updateHandler: (handlerId: number, properties: Partial<HandlerProperties>) => void;
  removeHandler: (handlerId: number) => void;
  reorderHandler: (handlerId: number, newIndex: number) => void;
  getHandlerId: (responseId: number) => number;
  getTriggerId: (responseId: number) => number;
  addResponse: (handlerId: number) => number;
  removeResponse: (responseId: number) => void;
  updateResponse: (responseId: number, properties: ResponseProperties) => void;
  reorderResponse: (responseId: number, newIndex: number) => void;
  getEffectBuildables: GetNodeEffectBuildables;
  getConditionBuildables: GetNodeConditionBuildables;
  getConditionBuildable: GetConditionBuildable;
  resolveEffectBuildables: ResolveEffectBuildables;
  resolveConditionBuildables: ResolveConditionBuildables;
  getCustomComponent: (
    nodeOrId: number | DesignerNode,
    // eventType: string,
  ) => TriggersComponent<NodeDesignerDescr> | undefined;
}>;

export const TriggerDesignsContext = createContext<TriggerDesignsContext | undefined>(undefined);

export const useTriggerDesigns = () => {
  const contextValue = useContext(TriggerDesignsContext);
  if (!contextValue) {
    throw new Error(`Expected TriggerDesignsProvider in component ancestry.`);
  }
  return contextValue;
};

export const useTriggersForNode = (nodeId: number): TriggerDetails[] =>
  useNautilusSelector((state) => triggersForNodeSelector(state, { nodeId }));

export const useTriggersForEventType = (nodeId: number, eventType: string): TriggerDetails[] =>
  useNautilusSelector((state) => triggersForEventTypeSelector(state, { nodeId, eventType }));
